// Utilities to export chat history or a single AI message to .docx
// Attempts to preserve Markdown formatting similar to the in-app renderer
import {
  AlignmentType,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  Document,
  BorderStyle,
  WidthType
} from 'docx';

// Extract "final message" content from a possibly structured/streamed payload
export const extractFinalMessageContent = (raw) => {
  if (!raw) return '';
  const text = String(raw).trim();
  if (!text.startsWith('{')) return String(raw);
  try {
    // Try direct JSON parse
    const obj = JSON.parse(text);
    if (obj && obj.final_message) return String(obj.final_message);
  } catch (e) {
    // Try brace-count extraction for complete JSON prefix
    try {
      let braceCount = 0;
      let end = -1;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === '{') braceCount++;
        if (text[i] === '}') {
          braceCount--;
          if (braceCount === 0) {
            end = i + 1;
            break;
          }
        }
      }
      if (end > 0) {
        const part = text.substring(0, end);
        const obj2 = JSON.parse(part);
        if (obj2 && obj2.final_message) return String(obj2.final_message);
      }
    } catch (braceParseError) {
      // ignore partial/invalid JSON during streaming
    }
    // Manual extraction of "final_message":"..."
    try {
      const idx = text.indexOf('"final_message"');
      if (idx !== -1) {
        const openQuote = text.indexOf('"', idx + '"final_message"'.length + 1);
        if (openQuote !== -1) {
          let pos = openQuote + 1;
          let out = '';
          while (pos < text.length) {
            const ch = text[pos];
            if (ch === '\\' && pos + 1 < text.length) {
              const n = text[pos + 1];
              if (n === 'n') {
                out += '\n';
                pos += 2;
                continue;
              }
              if (n === 't') {
                out += '\t';
                pos += 2;
                continue;
              }
              if (n === 'r') {
                out += '\r';
                pos += 2;
                continue;
              }
              if (n === '"') {
                out += '"';
                pos += 2;
                continue;
              }
              if (n === '\\') {
                out += '\\';
                pos += 2;
                continue;
              }
              out += n;
              pos += 2;
              continue;
            }
            if (ch === '"') break;
            out += ch;
            pos++;
          }
          if (out) return out;
        }
      }
    } catch (manualExtractError) {
      // ignore if manual extraction fails
    }
  }
  return String(raw);
};

// Basic inline Markdown splitter for bold/italic/code/links
const splitInline = (text) => {
  if (!text) return [{ text: '' }];
  const parts = [];
  let i = 0;
  const push = (obj) => parts.push(obj);

  while (i < text.length) {
    // Inline code `code`
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        const codeText = text.substring(i + 1, end);
        push({ text: codeText, code: true });
        i = end + 1;
        continue;
      }
    }
    // Bold **text**
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        const boldText = text.substring(i + 2, end);
        push({ text: boldText, bold: true });
        i = end + 2;
        continue;
      }
    }
    // Italic *text*
    if (text[i] === '*') {
      const end = text.indexOf('*', i + 1);
      if (end !== -1) {
        const italicText = text.substring(i + 1, end);
        push({ text: italicText, italics: true });
        i = end + 1;
        continue;
      }
    }
    // Link [text](url)
    if (text[i] === '[') {
      const closeBracket = text.indexOf(']', i + 1);
      if (closeBracket !== -1 && text[closeBracket + 1] === '(') {
        const closeParen = text.indexOf(')', closeBracket + 2);
        if (closeParen !== -1) {
          const label = text.substring(i + 1, closeBracket);
          const url = text.substring(closeBracket + 2, closeParen);
          push({ text: label, link: url });
          i = closeParen + 1;
          continue;
        }
      }
    }
    // Plain char run until next special token
    let j = i + 1;
    while (j < text.length && !'`*['.includes(text[j])) j++;
    push({ text: text.substring(i, j) });
    i = j;
  }
  return parts;
};

const paragraphFromInline = (text, opts = {}) => {
  const runs = splitInline(text).map(
    (part) =>
      new TextRun({
        text: part.text,
        bold: !!part.bold,
        italics: !!part.italics,
        font: part.code ? 'Consolas' : undefined
      })
  );
  return new Paragraph({ children: runs, ...opts });
};

const createDivider = () =>
  new Paragraph({
    children: [new TextRun({ text: '—'.repeat(48) })],
    alignment: AlignmentType.CENTER
  });

const createTable = (headers, rows) => {
  const totalWidth = 9000;
  const colCount = headers.length;
  const colWidth = Math.floor(totalWidth / Math.max(colCount, 1));
  const headerRow = new TableRow({
    children: headers.map(
      (h) =>
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
          width: { size: colWidth, type: WidthType.DXA },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 4 },
            bottom: { style: BorderStyle.SINGLE, size: 4 },
            left: { style: BorderStyle.SINGLE, size: 4 },
            right: { style: BorderStyle.SINGLE, size: 4 }
          }
        })
    )
  });
  const bodyRows = rows.map(
    (row) =>
      new TableRow({
        children: row.map(
          (cell) =>
            new TableCell({
              children: [new Paragraph({ text: String(cell ?? '') })],
              width: { size: colWidth, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 4 },
                bottom: { style: BorderStyle.SINGLE, size: 4 },
                left: { style: BorderStyle.SINGLE, size: 4 },
                right: { style: BorderStyle.SINGLE, size: 4 }
              }
            })
        )
      })
  );
  return new Table({ rows: [headerRow, ...bodyRows], width: { size: totalWidth, type: WidthType.DXA } });
};

// Parse Markdown into docx blocks (Paragraphs/Tables)
const markdownToBlocks = (md) => {
  const lines = String(md || '')
    .replace(/\r\n/g, '\n')
    .split('\n');
  const blocks = [];
  let i = 0;
  let inFence = false;
  // track fenced code block content only; language token not used by docx
  let fenceBuf = [];

  const flushFence = () => {
    if (!fenceBuf.length) return;
    const codeText = fenceBuf.join('\n');
    blocks.push(new Paragraph({ children: [new TextRun({ text: codeText, font: 'Consolas' })] }));
    fenceBuf = [];
  };

  // Simple table detection helpers
  const isDividerRow = (line) => {
    const cleaned = String(line || '').trim();
    if (!/\|/.test(cleaned)) return false;
    const cells = cleaned
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
    if (cells.length < 2) return false;
    return cells.every((c) => /^:?-{3,}:?$/.test(c));
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Code fence
    if (/^\s*```/.test(line)) {
      if (!inFence) {
        inFence = true; // start fenced code block; language token ignored
      } else {
        inFence = false;
        flushFence();
      }
      i++;
      continue;
    }
    if (inFence) {
      fenceBuf.push(line);
      i++;
      continue;
    }

    // Empty line -> paragraph spacer
    if (trimmed === '') {
      blocks.push(new Paragraph({ text: '' }));
      i++;
      continue;
    }

    // Headings #..######
    const hMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const text = hMatch[2];
      const hLevel = [
        HeadingLevel.HEADING_1,
        HeadingLevel.HEADING_2,
        HeadingLevel.HEADING_3,
        HeadingLevel.HEADING_4,
        HeadingLevel.HEADING_5,
        HeadingLevel.HEADING_6
      ][Math.min(level, 6) - 1];
      blocks.push(new Paragraph({ children: [new TextRun({ text, bold: true })], heading: hLevel }));
      i++;
      continue;
    }

    // Blockquote >
    if (/^>\s?/.test(trimmed)) {
      const content = trimmed.replace(/^>\s?/, '');
      blocks.push(paragraphFromInline(content, { indent: { left: 480 }, spacing: { before: 120, after: 120 }, italics: true }));
      i++;
      continue;
    }

    // Table: header | divider | rows
    if (trimmed.includes('|')) {
      const headerLine = trimmed;
      const next = lines[i + 1] || '';
      if (isDividerRow(next)) {
        const headers = headerLine
          .replace(/^\|/, '')
          .replace(/\|$/, '')
          .split('|')
          .map((c) => c.trim());
        i += 2;
        const rows = [];
        while (i < lines.length) {
          const rowLine = lines[i];
          if (!rowLine.includes('|') || rowLine.trim() === '') break;
          rows.push(
            rowLine
              .replace(/^\|/, '')
              .replace(/\|$/, '')
              .split('|')
              .map((c) => c.trim())
          );
          i++;
        }
        blocks.push(createTable(headers, rows));
        continue;
      }
    }

    // Lists
    if (/^[-*+]\s+/.test(trimmed)) {
      // Collect contiguous list items
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i])) {
        const txt = lines[i].replace(/^\s*[-*+]\s+/, '');
        blocks.push(paragraphFromInline(txt, { bullet: { level: 0 } }));
        i++;
      }
      continue;
    }
    if (/^\d+\.\s+/.test(trimmed)) {
      // Fallback to prefixed paragraphs to avoid requiring numbering config
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const original = lines[i];
        const match = original.match(/^(\s*\d+\.)\s+(.*)$/);
        const prefix = match ? match[1] + ' ' : '';
        const txt = match ? match[2] : original.replace(/^\s*\d+\.\s+/, '');
        blocks.push(paragraphFromInline(prefix + txt));
        i++;
      }
      continue;
    }

    // Fallback paragraph with inline formatting
    blocks.push(paragraphFromInline(line));
    i++;
  }

  return blocks;
};

export async function exportSingleMessageDocx(message, fileName = 'assistant-message.docx') {
  const isAi = message?.type !== 'user';
  const raw = String(message?.content || '');
  const content = isAi ? extractFinalMessageContent(raw) : raw;

  const children = [];
  children.push(new Paragraph({ children: [new TextRun({ text: isAi ? 'Assistant' : 'User', bold: true })] }));
  children.push(new Paragraph({ text: '' }));
  markdownToBlocks(content).forEach((b) => children.push(b));

  const doc = new Document({
    sections: [{ children }]
  });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportChatHistoryDocx(messages, fileName = 'chat-history.docx') {
  const children = [];
  children.push(new Paragraph({ children: [new TextRun({ text: 'Chat Export', bold: true })], heading: HeadingLevel.HEADING_1 }));
  children.push(createDivider());

  (messages || []).forEach((m, idx) => {
    const isAi = m.type !== 'user';
    const raw = String(m.content || '');
    const content = isAi ? extractFinalMessageContent(raw) : raw;
    const who = isAi ? 'Assistant' : 'User';

    children.push(
      new Paragraph({
        children: [new TextRun({ text: `${who}`, bold: true })]
      })
    );
    children.push(new Paragraph({ text: '' }));
    markdownToBlocks(content).forEach((b) => children.push(b));
    if (idx < messages.length - 1) children.push(createDivider());
  });

  const doc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default {
  exportSingleMessageDocx,
  exportChatHistoryDocx,
  extractFinalMessageContent
};
